import { useQuery } from "@tanstack/react-query";
import React from "react";
import api from "../../utils/axios.config";
import Loading from "../Shared/Loading";
import { CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

const PaymentSummary = () => {
  const { isLoading, error, data } = useQuery({
    queryKey: ["paymentSummary"],
    queryFn: async () => await api.get("/payments/summary"),
  });

  const summary = data?.data?.data;

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="text-center py-10 text-red-500">
        Error loading payment summary
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold text-center text-primary mb-8">
        Payment Summary
      </h2>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            Current Status:{" "}
            <span
              className={`font-semibold ${
                summary?.currentMonthStatus === "paid"
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {summary?.currentMonthStatus === "paid" ? "Paid" : "Unpaid"}
            </span>
          </h3>
          <div className="text-lg font-medium text-gray-900">
            Monthly Fee:{" "}
            <span className="font-semibold">{summary?.monthlyFee} BDT</span>
          </div>
        </div>

        {summary?.currentMonthStatus === "unpaid" && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Your payment for this month is pending. Please submit your
                  payment to avoid late fees.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/dashboard/submit-payment"
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Submit Payment Now
              </Link>
            </div>
          </div>
        )}
      </div>

      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Last 6 Months Payment History
      </h3>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Month
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Amount
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {summary?.paymentHistory?.map((month) => (
                <tr key={month.month} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {new Date(
                        month.month.replace(/(\d{1,2})-(\d{4})/, "$2-$1") +
                          "-01"
                      ).toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {month.paid ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 flex items-center">
                        <CheckCircle2 className="mr-1" size={14} />
                        Paid
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 flex items-center">
                        <XCircle className="mr-1" size={14} />
                        Unpaid
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {month.amount} BDT
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {month.paid ? null : (
                      <Link
                        to="/dashboard/submit-payment"
                        className="text-primary hover:text-primary-dark"
                      >
                        Pay Now
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentSummary;
